/**
 * AuditHistoryTimeline Component
 *
 * Displays a visual timeline of everything recorded about one user
 * Fetches from /api/v1/platform/users/:userId/audit-history
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  ClockIcon,
  UserIcon,
  UserPlusIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowRightIcon,
  ArrowRightOnRectangleIcon,
  ArrowLeftOnRectangleIcon,
  ArrowPathIcon,
  KeyIcon,
  NoSymbolIcon,
  PencilSquareIcon,
  ExclamationTriangleIcon,
  EnvelopeIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../../config/api';

// Origin without the /api suffix API_BASE_URL carries, because the fetch below builds its own
// /api/v1 path. Read through the shared config rather than import.meta so this module is loadable
// outside Vite, and so importing it does not make its consumers unloadable either.
const API_URL = API_BASE_URL.replace(/\/api$/, '');

type AuditAction =
  | 'role_change'
  | 'permission_change'
  | 'invited'
  | 'created'
  | 'deleted'
  | 'activated'
  | 'deactivated'
  | 'updated'
  | 'login'
  | 'logout'
  | 'password_change'
  | 'password_reset_sent'
  | 'password_reset_complete'
  | 'two_factor'
  | 'access_denied'
  | 'other';

// Mirrors the service's actor relation; both unions have to change together.
type ActorRelation =
  | 'invited'
  | 'created'
  | 'updated'
  | 'deleted'
  | 'activated'
  | 'reactivated'
  | 'restored'
  | 'deactivated'
  | 'changed'
  | 'granted'
  | 'revoked'
  | 'modified'
  | 'requested'
  | 'reset'
  | 'signed_in'
  | 'signed_out'
  | 'enabled'
  | 'disabled'
  | 'verified'
  | 'generated'
  | 'attempted';

interface AuditEvent {
  id: string;
  timestamp: string;
  action: AuditAction;
  success: boolean;
  before?: string;
  after?: string;
  detail?: string;
  changedBy: {
    id: string;
    email?: string;
    firstName?: string;
    lastName?: string;
  } | null;
  actorRelation?: ActorRelation;
  ipAddress?: string;
}

interface AuditHistoryTimelineProps {
  userId: string;
  userName: string;
  onClose: () => void;
}

const ACTION_LABELS: Record<AuditAction, string> = {
  role_change: 'Role changed',
  permission_change: 'Permissions changed',
  invited: 'User invited',
  created: 'User created',
  deleted: 'User deleted',
  activated: 'User activated',
  deactivated: 'User deactivated',
  updated: 'User updated',
  login: 'Signed in',
  logout: 'Signed out',
  password_change: 'Password changed',
  password_reset_sent: 'Password reset email sent',
  password_reset_complete: 'Password reset completed',
  two_factor: 'Two-factor authentication changed',
  access_denied: 'Access denied',
  other: 'Recorded event'
};

const ACTOR_RELATION_LABELS: Record<ActorRelation, string> = {
  invited: 'Invited by',
  created: 'Created by',
  updated: 'Updated by',
  deleted: 'Deleted by',
  activated: 'Activated by',
  reactivated: 'Reactivated by',
  restored: 'Restored by',
  deactivated: 'Deactivated by',
  changed: 'Changed by',
  granted: 'Granted by',
  revoked: 'Revoked by',
  modified: 'Modified by',
  requested: 'Requested by',
  reset: 'Reset by',
  signed_in: 'Signed in by',
  signed_out: 'Signed out by',
  enabled: 'Enabled by',
  disabled: 'Disabled by',
  verified: 'Verified by',
  generated: 'Generated by',
  attempted: 'Attempted by'
};

// A row recorded under an action the service does not yet relate reaches here without one.
const DEFAULT_ACTOR_RELATION_LABEL = 'Changed by';

export const AuditHistoryTimeline: React.FC<AuditHistoryTimelineProps> = ({
  userId,
  userName,
  onClose
}) => {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [partial, setPartial] = useState(false);

  const fetchAuditHistory = useCallback(async (before?: string) => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }

    try {
      if (before) setLoadingOlder(true);
      else setLoading(true);
      setError(null);

      const query = before ? `?before=${encodeURIComponent(before)}` : '';
      const response = await fetch(
        `${API_URL}/api/v1/platform/users/${userId}/audit-history${query}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch audit history: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to load audit history');
      }

      // A payload without a history array is a broken contract, not an empty history.
      if (!Array.isArray(result.data?.history)) {
        throw new Error('Audit history response was not in the expected format');
      }

      const page: AuditEvent[] = result.data.history;
      setEvents((current) => {
        if (!before) return page;
        // The cursor is inclusive, so the boundary event comes back on both pages.
        const seen = new Set(current.map((e) => e.id));
        return [...current, ...page.filter((e) => !seen.has(e.id))];
      });
      setHasMore(result.data.hasMore === true);
      setPartial(result.data.partial === true);
    } catch (err) {
      console.error('Error fetching audit history:', err);
      setError(err instanceof Error ? err.message : 'Failed to load audit history');
    } finally {
      setLoading(false);
      setLoadingOlder(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchAuditHistory();
  }, [fetchAuditHistory]);

  const loadOlder = () => {
    const oldest = events.reduce<string | undefined>(
      (min, e) => (!min || e.timestamp < min ? e.timestamp : min),
      undefined
    );
    if (oldest) fetchAuditHistory(oldest);
  };

  const getActionIcon = (event: AuditEvent) => {
    if (!event.success) {
      return <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />;
    }

    switch (event.action) {
      case 'role_change':
        return <ShieldCheckIcon className="h-5 w-5 text-purple-600" />;
      case 'permission_change':
        return <KeyIcon className="h-5 w-5 text-purple-600" />;
      case 'activated':
        return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
      case 'deactivated':
        return <XCircleIcon className="h-5 w-5 text-yellow-600" />;
      case 'invited':
        return <UserPlusIcon className="h-5 w-5 text-blue-600" />;
      case 'created':
        return <UserIcon className="h-5 w-5 text-blue-600" />;
      case 'deleted':
        return <XCircleIcon className="h-5 w-5 text-red-600" />;
      case 'updated':
        return <PencilSquareIcon className="h-5 w-5 text-blue-600" />;
      case 'login':
        return <ArrowRightOnRectangleIcon className="h-5 w-5 text-teal-600" />;
      case 'logout':
        return <ArrowLeftOnRectangleIcon className="h-5 w-5 text-teal-600" />;
      case 'password_change':
        return <KeyIcon className="h-5 w-5 text-amber-600" />;
      case 'password_reset_complete':
        return <ArrowPathIcon className="h-5 w-5 text-amber-600" />;
      case 'password_reset_sent':
        return <EnvelopeIcon className="h-5 w-5 text-amber-600" />;
      case 'two_factor':
        return <ShieldCheckIcon className="h-5 w-5 text-amber-600" />;
      case 'access_denied':
        return <NoSymbolIcon className="h-5 w-5 text-red-600" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const getActionColor = (event: AuditEvent) => {
    if (!event.success) return 'bg-red-100 border-red-400';

    switch (event.action) {
      case 'role_change':
      case 'permission_change':
        return 'bg-purple-100 border-purple-300';
      case 'activated':
        return 'bg-green-100 border-green-300';
      case 'deactivated':
        return 'bg-yellow-100 border-yellow-300';
      case 'invited':
      case 'created':
      case 'updated':
        return 'bg-blue-100 border-blue-300';
      case 'deleted':
        return 'bg-red-100 border-red-300';
      case 'login':
      case 'logout':
        return 'bg-teal-100 border-teal-300';
      case 'password_change':
      case 'password_reset_complete':
      case 'two_factor':
        return 'bg-amber-100 border-amber-300';
      case 'password_reset_sent':
        return 'bg-orange-100 border-orange-300';
      default:
        return 'bg-gray-100 border-gray-300';
    }
  };

  const formatActionText = (event: AuditEvent) => {
    if (event.action === 'role_change' && (event.before || event.after)) {
      return (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
            {event.before || 'none'}
          </span>
          <ArrowRightIcon className="h-4 w-4 text-gray-400" />
          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800">
            {event.after || 'none'}
          </span>
        </div>
      );
    }

    return <span className="text-gray-800">{ACTION_LABELS[event.action] || event.action}</span>;
  };

  const getActorRelationLabel = (relation?: ActorRelation) =>
    (relation && ACTOR_RELATION_LABELS[relation]) || DEFAULT_ACTOR_RELATION_LABEL;

  const getChangedByName = (changedBy: AuditEvent['changedBy']) => {
    if (!changedBy) return 'Unknown';
    if (changedBy.firstName && changedBy.lastName) {
      return `${changedBy.firstName} ${changedBy.lastName}`;
    }
    return changedBy.email || changedBy.id;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Audit History</h2>
              <p className="mt-1 text-sm text-gray-600">{userName}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Outside the states below: an incomplete read has to be visible even when what came
              back is empty, which is indistinguishable from a healthy empty history. */}
          {!loading && !error && partial && (
            <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
              <span>Part of this user&apos;s history could not be read. Some events may be missing.</span>
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center space-y-3">
                <svg className="animate-spin h-8 w-8 text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="text-gray-600">Loading audit history...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <p className="text-red-600 mb-4">{error}</p>
                <button
                  onClick={() => fetchAuditHistory()}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : events.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center text-gray-500">
                <ClockIcon className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                <p>
                  {partial
                    ? 'No events came back from the source that responded'
                    : 'No audit history available for this user'}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Timeline */}
              <div className="relative">
                {events.map((event, index) => (
                  <div key={event.id} className="relative pb-8">
                    {/* Vertical line */}
                    {index < events.length - 1 && (
                      <div className="absolute left-4 top-8 -ml-px h-full w-0.5 bg-gray-200" />
                    )}

                    {/* Event */}
                    <div className="relative flex items-start space-x-3">
                      {/* Icon */}
                      <div
                        data-testid="event-icon"
                        className={`relative flex h-8 w-8 items-center justify-center rounded-full border-2 ${getActionColor(event)}`}
                      >
                        {getActionIcon(event)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900 mb-1 flex items-center gap-2 flex-wrap">
                            {formatActionText(event)}
                            {!event.success && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-800 border border-red-300">
                                Failed
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 space-y-1">
                            <div className="flex items-center">
                              <UserIcon className="h-3 w-3 mr-1" />
                              {`${getActorRelationLabel(event.actorRelation)}: ${getChangedByName(event.changedBy)}`}
                            </div>
                            <div className="flex items-center">
                              <ClockIcon className="h-3 w-3 mr-1" />
                              {new Date(event.timestamp).toLocaleString()}
                            </div>
                            {event.ipAddress && (
                              <div className="flex items-center">
                                <GlobeAltIcon className="h-3 w-3 mr-1" />
                                {event.ipAddress}
                              </div>
                            )}
                            {event.detail && (
                              <div className="text-gray-600 break-words mt-1">
                                {event.detail}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {hasMore && (
                <div className="text-center pt-4 border-t border-gray-200">
                  <button
                    onClick={loadOlder}
                    disabled={loadingOlder}
                    className="px-4 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    {loadingOlder ? 'Loading…' : 'Load older events'}
                  </button>
                  <p className="mt-2 text-xs text-gray-500">Showing {events.length} events.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuditHistoryTimeline;
